import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractBrand } from './FilterPanel';

// All SVGs share viewBox="0 0 24 24" so CSS width:100%;height:auto scales every
// logo identically within the square card — no stretching or clipping.
// Icon logos use geometric paths. Wordmark logos use text centered at (12,12) with
// font sizes calibrated so text fills ~70% of the viewBox width at natural proportions
// (no textLength forcing = no horizontal distortion).
const BRAND_CATALOG = [
  {
    key: 'Hercles Global',
    label: 'Hercles Global',
    color: '#222',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/hercles-logo.png'}
        alt="Hercles Global logo"
        style={{ width: '112%', height: '112%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'Apple',
    label: 'Apple',
    color: '#222',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/apple-logo.png'}
        alt="Apple logo"
        style={{ width: '120%', height: '120%', objectFit: 'contain', display: 'block', margin: 'auto', transform: 'translateY(-8px)' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'Samsung',
    label: 'Samsung',
    color: '#1428A0',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/samsung-logo.png'}
        alt="Samsung logo"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'Sony',
    label: 'Sony',
    color: '#111',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/sony-logo.png'}
        alt="Sony logo"
        style={{ width: '105%', height: '105%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'HP',
    label: 'HP',
    color: '#222',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/hp-logo.png'}
        alt="HP logo"
        style={{ width: '90%', height: '90%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'Dell',
    label: 'Dell',
    color: '#007DB8',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/dell-logo.png'}
        alt="Dell logo"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'Lenovo',
    label: 'Lenovo',
    color: '#E2231A',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/lenovo-lg.jpg'}
        alt="Lenovo logo"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'LG',
    label: 'LG',
    color: '#A50034',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/lg-logo.png'}
        alt="LG logo"
        style={{ width: '105%', height: '105%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'JBL',
    label: 'JBL',
    color: '#F96800',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/jbl-logo.jpg'}
        alt="JBL logo"
        style={{ width: '82%', height: '82%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
  {
    key: 'Xiaomi',
    label: 'Xiaomi',
    color: '#FF6900',
    svg: (
      <img
        src={process.env.PUBLIC_URL + '/xiaomi-logo.jpg'}
        alt="Xiaomi logo"
        style={{ width: '75%', height: '75%', objectFit: 'contain', display: 'block', margin: 'auto' }}
        aria-hidden="true"
      />
    ),
  },
];

function BrandStrip({ categories }) {
  const navigate = useNavigate();

  // Build a map: brandKey → first matching product's navigation target
  const brandMap = useMemo(() => {
    if (!categories || categories.length === 0) return {};
    const allProducts = categories.flatMap(c => c.products);
    const map = {};
    for (const p of allProducts) {
      const brand = extractBrand(p.name);
      if (brand !== 'Other' && !map[brand]) {
        // Find the category this product belongs to
        const cat = categories.find(c => c.products.some(q => q.id === p.id));
        map[brand] = { categoryId: cat?.id, categorySlug: cat?.slug, categoryName: cat?.name };
      }
    }
    return map;
  }, [categories]);

  const handleBrandClick = (brandKey) => {
    const target = brandMap[brandKey];
    if (target?.categorySlug) {
      navigate(`/category/${target.categorySlug}`, {
        state: { presetBrand: brandKey },
      });
    } else if (target?.categoryId) {
      // Fallback to ID if slug not available
      navigate(`/category/${target.categoryId}`, {
        state: { presetBrand: brandKey },
      });
    } else {
      // If brand not found in data, still navigate to home with a brand filter hint
      navigate('/', { state: { presetBrand: brandKey } });
    }
  };

  // Only show brands that are present in the product catalogue
  const visibleBrands = BRAND_CATALOG.filter(b => brandMap[b.key]);
  // Fallback: if none resolved (data still loading), show all 10 as greyed out
  const displayBrands = visibleBrands.length >= 5 ? visibleBrands : BRAND_CATALOG;

  return (
    <section className="brand-strip-section" aria-label="Shop by Brand">
      <div className="brand-strip-header">
        <h2 className="brand-strip-title">Shop by Brand</h2>
        <span className="brand-strip-sub">Browse products from your favourite brands</span>
      </div>
      <div className="brand-strip-grid" style={{ gridTemplateColumns: `repeat(${displayBrands.length}, 96px)` }}>
        {displayBrands.map(brand => {
          const available = !!brandMap[brand.key];
          return (
            <button
              key={brand.key}
              className={`brand-card${available ? '' : ' brand-card--ghost'}`}
              onClick={() => available && handleBrandClick(brand.key)}
              aria-label={`Shop ${brand.label} products`}
              style={{ '--brand-color': brand.color }}
              disabled={!available}
              title={brand.label}
            >
              <div className="brand-card-logo">
                {brand.svg}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default BrandStrip;
